import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { TableCell, TableHeader, TableKit, TableRow } from '@tiptap/extension-table';
import Underline from '@tiptap/extension-underline';
import StarterKit from '@tiptap/starter-kit';

/**
 * The schema behind docs/05-ADMIN-DASHBOARD.md §4.2's toolbar.
 *
 * The list of marks and nodes here is the *first* of two allowlists, and the
 * weaker one. §4.2 is explicit that "Output is sanitised server-side on every
 * write (`HtmlSanitizer`, HTMLPurifier allowlist). Non-negotiable" — CLAUDE.md
 * rule 6 says the same and explains why: the site being replaced is currently
 * serving injected spam links, and migrated content is the attack vector.
 *
 * So this editor is a *convenience*, not a control. It stops an editor pasting
 * a `<script>` and wondering why it vanished later; it is not what stops an API
 * client sending one. Anything added here that HTMLPurifier does not allow will
 * be silently dropped on save, which is the failure mode to watch for — the two
 * lists have to be kept in step, and the server's is the one that decides.
 */
export const richTextExtensions = [
  StarterKit.configure({
    /*
     * §2.1's type scale starts pages at `h1` and the editor is writing a page
     * *body*, so the document's own heading is not one of these — §4.2 offers
     * H2, H3 and H4 and nothing else.
     */
    heading: { levels: [2, 3, 4] },

    // `Link` is configured separately below, so StarterKit's copy would be a
    // second registration of the same mark.
    link: false,
  }),

  Underline,

  /*
   * §4.2's "document link (from library)" needs the anchor to carry the document's
   * id, and a mark keeps only the attributes it declares — passing
   * `data-document-id` to `setLink` without this would silently drop it, leaving a
   * link that works and a reference nothing can trace.
   */
  Link.extend({
    addAttributes() {
      return {
        ...this.parent?.(),

        documentId: {
          default: null,
          parseHTML: (element) => element.getAttribute('data-document-id'),
          renderHTML: (attributes) =>
            attributes.documentId === null
              ? {}
              : { 'data-document-id': String(attributes.documentId) },
        },
      };
    },
  }).configure({
    openOnClick: false,
    /*
     * `http`, `https` and `mailto` only. A `javascript:` href is the oldest
     * XSS there is, and while `HtmlSanitizer` strips it on write, an editor
     * should not be able to create one and see it apparently work.
     */
    protocols: ['http', 'https', 'mailto'],
    autolink: true,
    HTMLAttributes: {
      // Applied to what the editor produces; the public site's `RichText`
      // enforces the same on render, because migrated HTML has neither.
      rel: 'noopener noreferrer',
    },
  }),

  /*
   * §4.2's "image (from media library)".
   *
   * Extended with `data-media-id`, which is the difference between an image in a
   * body and a *reference* to one: `MediaUsage` reads that attribute, so deleting
   * a photograph an article uses halfway down its body is refused with a 409
   * naming the article. Without it the src would be a URL nobody could trace, and
   * the library's whole promise — that an image in use cannot vanish — would stop
   * at the edge of a rich-text field.
   *
   * `allowBase64` stays off. A pasted screenshot would otherwise arrive as a
   * megabyte of base64 inside the body column, un-resizable, uncacheable, and
   * invisible to the library; the toolbar's button is the way in.
   */
  Image.extend({
    addAttributes() {
      return {
        ...this.parent?.(),

        mediaId: {
          default: null,
          // The name in the markup differs from the name in the schema, which is
          // what these two say. `parseHTML` is what makes an existing body
          // round-trip rather than losing the id the moment somebody edits.
          parseHTML: (element) => element.getAttribute('data-media-id'),
          renderHTML: (attributes) =>
            attributes.mediaId === null ? {} : { 'data-media-id': String(attributes.mediaId) },
        },

        loading: { default: 'lazy' },
        decoding: { default: 'async' },
      };
    },
  }).configure({ allowBase64: false }),

  // §4.2 lists a table button. `TableKit` brings the node and the commands;
  // the three cell types are registered explicitly so a document with a
  // `<thead>` round-trips rather than being flattened into plain rows.
  TableKit.configure({ table: { resizable: true } }),
  TableRow,
  TableHeader,
  TableCell,
];

/**
 * The attributes a document link carries — docs/05-ADMIN-DASHBOARD.md §4.2.
 *
 * A function rather than an inline object at the two call sites, for a reason
 * worth knowing: TipTap types `setLink` from the *base* mark, so an inline literal
 * with `documentId` is an excess-property error while the same object through a
 * variable is accepted. Relying on that difference would mean the toolbar compiles
 * and a test of the same call does not. One typed helper is the shape, once.
 */
export function documentLinkAttributes(documentId: number): {
  href: string;
  documentId: string;
} {
  return {
    // §6 never exposes the storage path; the controller is also what counts the
    // download the client asked to be able to report on.
    href: `/api/v1/documents/${documentId}/download`,
    documentId: String(documentId),
  };
}
